<template>
  <div
    v-loading="isInitializing"
    class="wizard full-height px-3"
    style="position: relative"
  >
    <!-- Start main content area -->
    <div class="main-content">
      <!-- Start show a message if there is another CRM enabled -->
      <template v-if="otherCrmIsEnabled && !isInitializing">
        <el-row class="mx-2">
          <div class="p-3" style="border: 1px solid grey; border-radius: 5px">
            <i class="el-icon-connection mr-1"></i>
            <span>
              You can only connect <b>one CRM at a time</b>. Please
              <b>disable the other CRM</b> to continue.
            </span>
          </div>
        </el-row>
      </template>
      <!-- End show a message if there is another CRM enabled -->

      <!-- Start show the tab headers and content if the user has no other CRM enabled and the access token is set -->
      <template v-if="!otherCrmIsEnabled && hasAccessToken && !isInitializing">
        <!-- Start Tab Headers -->
        <el-tabs
          class="tab-head"
          v-model="activeTab"
          ref="integrationTabs"
          @tab-click="handleTabClick"
        >
          <el-tab-pane label="Connection" name="connection">
            <settings-connection-info-pane
              :model="model"
              :integration-name="integrationName"
              :integration-name-with-casing="integrationNameWithCasing"
              :other-crm-is-enabled="otherCrmIsEnabled"
              :has-access-token="hasAccessToken"
              :save-settings="saveSettings"
              :oauth="oauth"
            />
          </el-tab-pane>
          <el-tab-pane label="Sync" name="synchronization">
            <settings-sync-pane
              ref="syncPane"
              :model="model"
              :integration-name="integrationName"
              :integration-name-with-casing="integrationNameWithCasing"
              :statics="statics"
              :default-mapper-settings="defaultMapperSettings"
            />
          </el-tab-pane>
          <el-tab-pane
            v-for="pane in mappingPanes"
            :key="pane.name"
            :label="pane.label"
            :name="pane.name"
          >
            <component :is="pane.component" v-bind="pane.props" />
          </el-tab-pane>
          <el-tab-pane
            label="Contact Property Mapping"
            name="mapping"
            v-if="
              integrationName === GOHIGHLEVEL_INTEGRATION &&
              !hasGoHighLevelRequiredScope
            "
          >
            <div class="disabled-tab-content">
              <div class="disabled-tab-message">
                <i class="el-icon-warning"></i>
                <span
                  >Re-authenticate the integration to access Contact Property
                  Mapping functionality</span
                >
              </div>
              <el-button type="primary" @click="oauth()" class="mt-3">
                Re-authenticate Integration
              </el-button>
            </div>
          </el-tab-pane>
          <el-tab-pane
            label="Filters"
            name="filters"
            v-if="
              [HUBSPOT_INTEGRATION, GOHIGHLEVEL_INTEGRATION].includes(
                integrationName
              )
            "
          >
            <settings-filters-pane
              v-if="showFiltersPane"
              :model="model"
              :statics="statics"
              :integration-name-with-casing="integrationNameWithCasing"
              ref="filtersPane"
            />
          </el-tab-pane>
          <el-tab-pane
            label="Communication"
            name="communication"
            v-if="
              [HUBSPOT_INTEGRATION, SALESFORCE_INTEGRATION].includes(
                integrationName
              )
            "
          >
            <settings-communication-activity-pane
              :model="model"
              :integration-name="integrationName"
              :integration-name-with-casing="integrationNameWithCasing"
              :statics="statics"
              :default-mapper-settings="defaultMapperSettings"
              :default-file-upload-settings="defaultFileUploadSettings"
              v-if="integrationName === HUBSPOT_INTEGRATION"
            />
            <salesforce-fields-mapping
              :model="model"
              v-if="integrationName === SALESFORCE_INTEGRATION"
            />
          </el-tab-pane>
          <el-tab-pane
            label="User Mapping"
            name="user-mapping"
            v-if="integrationName === HUBSPOT_INTEGRATION"
          >
            <settings-user-mapping-pane
              :model="model"
              :integration-name="integrationName"
              :integration-name-with-casing="integrationNameWithCasing"
              :statics="statics"
            />
          </el-tab-pane>
          <el-tab-pane
            label="Channel Accounts"
            name="lines"
            v-if="integrationName === 'hubspot'"
          >
            <settings-lines-pane
              :model="model"
              :integration-name="integrationName"
              :integration-name-with-casing="integrationNameWithCasing"
              :static-name="statics.name"
            />
          </el-tab-pane>
          <el-tab-pane
            v-if="integrationName === HUBSPOT_INTEGRATION"
            label="AVA Entity Mapping"
            name="ava-entities"
          >
            <settings-ava-property-mapping-pane
              v-if="integrationName === HUBSPOT_INTEGRATION"
              :model="model"
              :integration-name="integrationName"
              :integration-name-with-casing="integrationNameWithCasing"
              :statics="statics"
              :default-mapper-settings="defaultMapperSettings"
            />
          </el-tab-pane>
        </el-tabs>
        <!-- End Tab Headers -->
      </template>
      <!-- End show the tab headers and content if the user has no other CRM enabled and the access token is set -->

      <!-- Start only show the Authenticate Button if the access token is not set -->
      <template v-if="!otherCrmIsEnabled && !hasAccessToken && !isInitializing">
        <hubspot-settings-workflow-notice
          class="mx-2"
          v-if="integrationName === 'hubspot'"
          :show-calling-note="true"
        />
        <salesforce-settings-authenticate
          :integration-name="integrationName"
          :card="model"
          :oauth="oauth"
          v-if="integrationName === SALESFORCE_INTEGRATION"
        />
        <settings-authenticate
          :integration-name-with-casing="integrationNameWithCasing"
          :oauth="oauth"
          v-else
        />
      </template>
      <!-- End only show the Authenticate button if the access token is not set -->

      <!-- Start action buttons-->
      <div
        v-if="!isInitializing && hasAccessToken && !otherCrmIsEnabled"
        class="action-buttons"
      >
        <div class="left-buttons">
          <el-button @click="handleBackTab" class="mr-2">
            <i class="el-icon-arrow-left"></i> Back
          </el-button>
          <el-button @click="handleNextTab">
            Next <i class="el-icon-arrow-right"></i>
          </el-button>
        </div>
        <div class="right-buttons">
          <el-tooltip
            :content="
              isResetButtonDisabled
                ? 'Reset not available for the current tab'
                : 'Reset the current tab settings to their default values'
            "
            placement="top"
          >
            <el-button
              class="mr-2"
              @click="handleResetTab"
              :disabled="isResetButtonDisabled"
              v-if="integrationName !== SALESFORCE_INTEGRATION"
              >Reset</el-button
            >
          </el-tooltip>
          <el-tooltip
            content="Save your changes on all settings tabs"
            placement="top"
          >
            <el-button type="success" @click="saveSettings" :loading="isSaving"
              >Save</el-button
            >
          </el-tooltip>
        </div>
      </div>
      <!-- End action buttons-->
    </div>
    <!-- End main content area -->
  </div>
</template>

<script>
import _, { clone } from 'lodash'
import { mapState, mapActions, mapGetters } from 'vuex'
import SettingsConnectionInfoPane from './settings-connection-info-pane.vue'
import SettingsFiltersPane from './settings-filters-pane.vue'
import { ContactType } from './settings-filters-pane.vue'
import SettingsSyncPane from './settings-sync-pane.vue'
import SettingsContactPropertyMappingPane from './settings-contact-property-mapping-pane.vue'
import SettingsCommunicationActivityPane from './settings-communication-activity-pane.vue'
import SettingsUserMappingPane from './settings-user-mapping-pane.vue'
import HubspotSettingsWorkflowNotice from './hubspot/settings-workflow-notice.vue'
import SettingsLinesPane from './hubspot/settings-lines-pane.vue'
import SettingsAvaPropertyMappingPane from './settings-ava-property-mapping-pane.vue'
import * as IntegrationPropertyMapDirections from '../../constants/integration-property-map-directions'
import {
  GOHIGHLEVEL_INTEGRATION,
  HUBSPOT_CALLING_INTEGRATION,
  HUBSPOT_INTEGRATION,
  SALESFORCE_INTEGRATION
} from '../../constants/integrations'
import SettingsAuthenticate from './settings-authenticate.vue'
import SalesforceSettingsAuthenticate from './salesforce/salesforce-settings-authenticate.vue'
import SalesforceFieldsMapping from './salesforce/salesforce-fields-mapping.vue'
import {
  MODULE_CONTACT,
  MODULE_LEAD,
  MODULE_ACCOUNT
} from '../../constants/integration_modules'
import { handleVuexApiCall, sleep } from '../../utils/integration-helpers'
export default {
  components: {
    SalesforceFieldsMapping,
    HubspotSettingsWorkflowNotice,
    SettingsConnectionInfoPane,
    SettingsFiltersPane,
    SettingsSyncPane,
    SettingsContactPropertyMappingPane,
    SettingsCommunicationActivityPane,
    SettingsUserMappingPane,
    SettingsAuthenticate,
    SalesforceSettingsAuthenticate,
    SettingsLinesPane,
    SettingsAvaPropertyMappingPane,
  },
  props: {
    integrationName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      SALESFORCE_INTEGRATION,
      HUBSPOT_INTEGRATION,
      GOHIGHLEVEL_INTEGRATION,
      // UI
      isInitializing: false,
      isSaving: false,
      activeTab: 'connection',
      showFiltersPane: true,

      // Default settings
      defaultMapperSettings: {
        user: null,
        contact: null,
        call: null,
        sms: null,
        ava: null
      },
      defaultFileUploadSettings: null,

      // static data
      statics: {
        logo: null,
        logo_inverse: null,
        logo_square: null,
        logo_square_inverse: null,
        host: null,
        referer: null,
        name: null,
        domain: null,
        whitelabel: false,
        path: null
      },

      // model data from and for the API
      model: {
        status: false,
        credentials: {
          tracking_id: '',
          api_token: '',
          access_token: '',
          access_key: '',
          access_key_secret: '',
          uiDomain: '', // HubSpot
          client_id: '', // Google
          client_secret: '' // Google
        },
        settings: {
          report_outbound: false,
          lead_status_locked: false,
          mailbox_id: '',
          workspace_id: '',
          page_id: null,
          workflow_id: null,
          page: null,
          sender_address: '',
          sender_name: '',
          events: [],
          domain: '',
          module: 'contacts',
          filters: null,
          send_sms_as_note: true,
          send_call_events: false,
          selected_contact: null,
          prefix: '',
          currently_saved_prefix: '',
          tags: [],
          encryption: 'tls',
          calendars: [],
          calendar: {
            id: null,
            timeZone: null
          },
          push_notification_channel: {
            status: false
          }
        },

        name: '',
        connection_status: 0,
        last_connection_error: '',
        updated_by: null,
        env: null
      },
      defaultPropertyMappings: [],
      customPropertyMappings: []
    }
  },
  computed: {
    ...mapGetters('hubspotPropertyMapping', [
      'getPropertyMappings',
      'getCrmContactProperties',
      'getAlowareContactProperties'
    ]),
    ...mapGetters('avaEntityMapping', [
      'getAvaEntityContactMappings'
    ]),
    ...mapState({
      current_company: (state) => state.cache.current_company
    }),
    ...mapGetters(['refreshPageIsNeeded', 'CRMModuleWasChanged']),
    mappingPanes() {
      let list = []
      let props = {
        model: this.model,
        integrationName: this.integrationName,
        integrationNameWithCasing: this.integrationNameWithCasing,
        statics: this.statics,
        module: MODULE_CONTACT,
        defaultPropertyMappings: this.defaultPropertyMappings,
        customPropertyMappings: this.customPropertyMappings
      }

      if (
        this.integrationName === SALESFORCE_INTEGRATION &&
        this.model.credentials?.feature_version >= 2
      ) {
        if (this.model.settings?.contact_settings?.enable_contact) {
          list.push({
            label: 'Contact Property Mapping',
            name: 'mapping',
            component: 'SettingsContactPropertyMappingPane',
            props
          })
        }

        if (this.model.settings?.contact_settings?.enable_lead) {
          let cloned_props = clone(props)
          cloned_props.module = MODULE_LEAD
          list.push({
            label: 'Lead Property Mapping',
            name: 'lead-mapping',
            component: 'SettingsContactPropertyMappingPane',
            props: cloned_props
          })
        }
        // Accounts mapping tab (feature v5)
        if (
          this.model.credentials?.feature_version >= 5 &&
          this.model.settings?.contact_settings?.enable_account
        ) {
          let cloned_props = clone(props)
          cloned_props.module = MODULE_ACCOUNT
          list.push({
            label: 'Account Property Mapping',
            name: 'account-mapping',
            component: 'SettingsContactPropertyMappingPane',
            props: cloned_props
          })
        }
      } else if (this.integrationName === GOHIGHLEVEL_INTEGRATION) {
        // For GoHighLevel, check if the required scope is present
        if (this.hasGoHighLevelRequiredScope) {
          list.push({
            label: 'Contact Property Mapping',
            name: 'mapping',
            component: 'SettingsContactPropertyMappingPane',
            props
          })
        }
        // Don't add the tab at all if scope is missing - it will be handled by a separate tab-pane
      } else {
        list.push({
          label: 'Contact Property Mapping',
          name: 'mapping',
          component: 'SettingsContactPropertyMappingPane',
          props
        })
      }

      return list
    },
    hasAccessToken() {
      switch (this.integrationName) {
        case SALESFORCE_INTEGRATION:
          return this.isMigrated
            ? this.model.credentials.token.access_token !== ''
            : this.model.credentials.access_token !== ''
        default:
          return Boolean(this.model.credentials.access_token)
      }
    },
    isMigrated() {
      return this.model.credentials.hasOwnProperty('is_migrated')
    },
    otherCrmIsEnabled() {
      let integrations = {
        pipedrive: 'pipedrive_integration_enabled',
        hubspot: 'hubspot_integration_enabled',
        zoho: 'zoho_integration_enabled',
        guesty: 'guesty_integration_enabled',
        salesforce: 'salesforce_integration_enabled'
      }

      // Aloware demo accounts are allowed to turn on multiples CRMs
      if (
        localStorage.getItem('aloware_demo_companies') &&
        localStorage
          .getItem('aloware_demo_companies')
          .split(',')
          .includes(this.current_company.id.toString())
      ) {
        return false
      }

      let otherIntegrationName = null
      for (let integration in integrations) {
        if (
          this.current_company &&
          this.current_company[integrations[integration]]
        ) {
          otherIntegrationName = integration
        }
      }

      if (
        otherIntegrationName &&
        integrations.hasOwnProperty(otherIntegrationName)
      ) {
        delete integrations[otherIntegrationName]
      }

      if (
        Object.keys(integrations).includes(this.integrationName) &&
        otherIntegrationName
      ) {
        return true
      }

      return false
    },
    integrationNameWithCasing() {
      if (this.integrationName === 'hubspot') {
        return 'HubSpot'
      }

      if (this.integrationName === GOHIGHLEVEL_INTEGRATION) {
        return 'HighLevel'
      }

      return _.upperFirst(this.integrationName)
    },
    isResetButtonDisabled() {
      if (this.activeTab === 'connection') return true
      if (this.activeTab === 'mapping') return true
      if (this.activeTab === 'user-mapping') return true
      if (this.activeTab === 'lines') return true
      if (this.activeTab === 'ava-entities') return true
      if (
        this.activeTab === 'filters' &&
        !this.model?.settings?.filters?.status
      )
        return true

      return false
    },
    availableTabs() {
      // Get the tabs component reference
      const tabsComponent = this.$refs.integrationTabs
      if (!tabsComponent) return []

      // Get all tab pane components that are actually rendered
      const panes = tabsComponent.$children.filter(
        (child) => child.$options.name === 'ElTabPane'
      )

      // Return array of tab names that are actually rendered
      return panes.map((pane) => pane.name)
    },
    hasGoHighLevelRequiredScope() {
      if (this.integrationName !== GOHIGHLEVEL_INTEGRATION) {
        return true
      }

      // Check if the required scope "objects/schema.readonly" is present in model.credentials.scopes
      const scopes = this.model.credentials?.scopes || []
      return scopes.includes('objects/schema.readonly')
    }
  },
  methods: {
    ...mapActions('hubspotPropertyMapping', [
      'fetchPropertyMappings',
      'fetchCrmContactProperties',
      'storePropertyMappings',
      'getIntegrationPropertyMappings',
      'getIntegrationItemProperties',
      'getAlowareDefaultProperties'
    ]),
    ...mapActions(['setCRMModuleWasChanged']),
    ...mapActions('cache', ['setCurrentCompany']),
    ...mapActions('avaEntityMapping', [
      'storeAvaEntityContactMappings',
      'fetchAvaEntityContactMappings',
      'getIntegrationContactProperties'
    ]),
    oauth(options = null) {
      if (!options) {
        options = {}
      }

      if (this.model.credentials?.instance) {
        options.instance = this.model.credentials?.instance
      }

      let params = ''
      if (options && Object.keys(options).length > 0) {
        params = `?${this.$options.filters.queryString(options)}`
      }

      window.location.href = `/integrations/${this.integrationName}/oauth${params}`
    },
    async initializeSettings() {
      let response = null

      try {
        response = await axios.get(
          `/api/v1/integration/${this.integrationName}`
        )

        if (this.integrationName === 'hubspot') {
          if (response.data.defaults?.mapper_settings)
            this.defaultMapperSettings = response.data.defaults.mapper_settings

          if (response.data.defaults?.file_upload_settings)
            this.defaultFileUploadSettings = response.data.defaults.file_upload_settings
        }
      } catch (error) {
        this.$root.handleErrors(error.response)
      }

      if (
        response.data.integration !== null &&
        response.data.integration.credentials &&
        response.data.integration.credentials !== 'null'
      ) {
        this.model.credentials = JSON.parse(
          response.data.integration.credentials
        )
      }

      if (
        response.data.integration !== null &&
        response.data.integration.settings &&
        response.data.integration.settings !== 'null'
      ) {
        this.model.settings = _.assign(
          {},
          this.model.settings,
          JSON.parse(response.data.integration.settings)
        )
      }

      // get information for this card
      switch (this.integrationName) {
        case HUBSPOT_INTEGRATION:
          this.model.status = this.current_company.hubspot_integration_enabled
          break
        case SALESFORCE_INTEGRATION:
          this.model.status =
            this.current_company.salesforce_integration_enabled
          break
        case GOHIGHLEVEL_INTEGRATION:
          this.model.status =
            this.current_company.gohighlevel_integration_enabled
          break
      }

      this.model.connection_status =
        response.data.integration?.connection_status || 0
      this.model.last_connection_error =
        response.data.integration?.last_connection_error || ''
      this.model.name = this.integrationName
      this.model.updated_by = response.data.integration?.updated_by
      this.model.show_access_token = response.data.show_access_token || false

      return response
    },
    async getStatics() {
      this.loading_whitelabel = true
      try {
        const response = await axios.get('/get-statics')
        this.statics = response.data
      } catch (error) {
        this.$root.handleErrors(error.response)
      } finally {
        this.loading_whitelabel = false
      }
    },
    removePropertyFromQuery(propertyName) {
      let query = Object.assign({}, this.$route.query)
      delete query[propertyName]
      this.$router.replace({ query })
    },
    /**
     * These are fallbacks for any validations that are not handled by the UI components
     * E.g. Call duration type is selected but call duration is not set.
     */
    validateModel() {
      // Reject if a call duration type is selected but no call duration set
      if (
        this.model.settings.filters.call_duration_type &&
        !this.model.settings.filters.call_duration
      ) {
        this.$notify({
          title: `${this.integrationNameWithCasing} Filters Validation Failed`,
          message:
            'Call duration type is selected but call duration is not set.',
          type: 'warning'
        })
        return false
      }

      // Reject if the filter is on but neither "All" or "Disposed Contacts" are selected
      if (
        this.model.settings.filters.status &&
        ![ContactType.DISPOSED, ContactType.ALL].includes(
          this.model.settings.filters.contacts
        )
      ) {
        this.$notify({
          title: `${this.integrationNameWithCasing} Filters Validation Failed`,
          message: 'Please select at least one Contact Type.',
          type: 'warning'
        })
        return false
      }

      // Reject if filter is on and no Communication Type option is selected
      if (
        this.model.settings.filters.status &&
        this.model.settings.filters.type.length < 1
      ) {
        this.$notify({
          title: `${this.integrationNameWithCasing} Filters Validation Failed`,
          message: 'Please select at least one Communication Type.',
          type: 'warning'
        })
        return false
      }

      // Reject if filter is on and no Communication Disposition Status option is selected
      if (
        this.model.settings.filters.status &&
        this.model.settings.filters.communication_disposition_statuses.length <
          1
      ) {
        this.$notify({
          title: `${this.integrationNameWithCasing} Filters Validation Failed`,
          message:
            'Please include at least one Communication Disposition Status.',
          type: 'warning'
        })
        return false
      }

      // Reject if SMS attachment upload is enabled but no folder is selected (HubSpot only)
      if (
        this.integrationName === HUBSPOT_INTEGRATION &&
        this.model.settings.file_upload_settings?.sms_attachments?.enabled &&
        (!this.model.settings.file_upload_settings.sms_attachments.folder_id ||
         !this.model.settings.file_upload_settings.sms_attachments.folder_name ||
         !this.model.settings.file_upload_settings.sms_attachments.folder_path)
      ) {
        this.$notify({
          title: `${this.integrationNameWithCasing} SMS Attachment Upload Error`,
          message: 'SMS attachment upload is enabled but no folder is selected. Please select a folder.',
          type: 'warning'
        })
        return false
      }

      // Reject if transcription upload is enabled but no folder is selected (HubSpot only)
      if (
        this.integrationName === HUBSPOT_INTEGRATION &&
        this.model.settings.file_upload_settings?.transcription_attachment?.enabled &&
        (!this.model.settings.file_upload_settings.transcription_attachment.folder_id ||
         !this.model.settings.file_upload_settings.transcription_attachment.folder_name ||
         !this.model.settings.file_upload_settings.transcription_attachment.folder_path)
      ) {
        this.$notify({
          title: `${this.integrationNameWithCasing} Transcription Upload Error`,
          message: 'Transcription upload is enabled but no folder is selected. Please select a folder.',
          type: 'warning'
        })
        return false
      }

      return true
    },
    async saveSettings() {
      this.isSaving = true

      try {
        this.model.name = this.integrationName

        // Validate if save is called and the integration is enabled
        // Filter is not available for Salesforce yet so ignore its validation
        if (
          this.model.status &&
          this.integrationName !== SALESFORCE_INTEGRATION
        ) {
          const isValid = this.validateModel()
          if (!isValid) return (this.isSaving = false)
        }

        // Check for conflicts between contact property mappings and AVA entity mappings
        if (this.model.status && this.hasMappingsConflictWithCrmKeys()) {
          this.$notify({
            duration: 5000,
            offset: 175,
            title: 'Mapping Conflict',
            message: `The same ${this.integrationNameWithCasing} property cannot be mapped in both Contact Property Mapping and AVA Entity Mapping.`,
            type: 'error',
            showClose: true
          })
          return (this.isSaving = false)
        }

        let contactPropertyMappingsSaved = false
        if (this.model.status && !this.CRMModuleWasChanged) {
          contactPropertyMappingsSaved = await this.saveContactPropertyMappings()
        }

        // Handle AVA Entity Mappings for HubSpot
        let avaEntityMappingsSaved = false
        if (this.model.status && this.integrationName === HUBSPOT_INTEGRATION) {
          avaEntityMappingsSaved = await this.saveAvaEntityMappings()
        }

        // Save the settings if property mappings are saved or the integration is being disabled or CRM module was changed
        if (
          // Check for both AVA and Contact mappings for HubSpot, and only check Contact mappings for others
          (this.integrationName === HUBSPOT_INTEGRATION ? avaEntityMappingsSaved && contactPropertyMappingsSaved : contactPropertyMappingsSaved) ||
          !this.model.status ||
          this.CRMModuleWasChanged
        ) {
          await axios.post('/api/v1/integration', {
            // send only useful payload
            name: this.model.name,
            status: this.model.status,
            settings: this.model.settings
          })

          if (this.model.status) {
            // Show a notification banner if the integration is enabled
            this.$notify({
              title: 'Success',
              message: `${_.upperFirst(
                this.integrationName
              )} settings saved successfully.`,
              type: 'success'
            })

            // Reload Integration contact properties for HubSpot after a successful save
            if (this.integrationName === HUBSPOT_INTEGRATION) {
              try {
                await Promise.all([
                  this.getIntegrationItemProperties(this.integrationName),
                  this.$store.dispatch('avaEntityMapping/getIntegrationContactProperties', this.integrationName)
                ])
              } catch (e) {
                console.error('Failed to reload mappings after save:', e)
              }
            }

            if (this.refreshPageIsNeeded) {
              window.location.reload()
            } else {
              if (this.CRMModuleWasChanged) {
                this.setCRMModuleWasChanged(false)
                await this.initializeCustomFields()
                // Refresh settings in salesforce-contact-settings component
                if (this.integrationName === SALESFORCE_INTEGRATION) {
                  this.$nextTick(() => {
                    const salesforceContactSettings =
                      this.$refs.syncPane?.$refs.salesforceContactSettings
                    if (salesforceContactSettings) {
                      salesforceContactSettings.resetSalesforceSettings()
                    }
                  })
                }
              }
            }
          }
        }
      } catch (err) {
        console.error(err)
        this.$root.handleErrors(err.response)
      } finally {
        this.isSaving = false
      }
    },

    /**
     * Save contact property mappings
     *
     * @returns {Promise<boolean>}
     */
    async saveContactPropertyMappings() {
      const mergedMappings = this.defaultPropertyMappings.concat(
        this.customPropertyMappings
      )

      // in case the user spams the save button while things are loading
      if (mergedMappings.length === 0) return true

      const errorNotifDuration = 5 * 1000
      if (!this.hasCompleteMappings(mergedMappings)) {
        this.$notify({
          duration: errorNotifDuration,
          offset: 175,
          title: 'Contact Property Mapping',
          message: 'One or more property mappings have no selected value',
          type: 'error',
          showClose: true
        })
        return false
      }

      const payload = {
        mappings: mergedMappings,
        integration: this.integrationName
      }
      const res = await handleVuexApiCall(this.storePropertyMappings, payload)

      if (!res.success) {
        let errors = res.response?.data?.errors ?? {}

        // Format error messages for display
        let errorMessages = []
        if (typeof errors === 'object') {
          Object.entries(errors).forEach(([key, messages]) => {
            if (Array.isArray(messages)) {
              errorMessages.push(...messages)
            } else if (typeof messages === 'string') {
              errorMessages.push(messages)
            }
          })
        }

        this.$notify({
          duration: errorNotifDuration,
          offset: 175,
          title: 'Property Mapping Errors',
          message:
            errorMessages.length > 0
              ? `<ul style="margin: 0; padding-left: 20px;">${errorMessages
                  .map((msg) => `<li>${msg}</li>`)
                  .join('')}</ul>`
              : 'Unable to save property mappings. Please contact customer support',
          type: 'error',
          showClose: true,
          dangerouslyUseHTMLString: true
        })

        return false
      }

      return true
    },

    /**
     * Save AVA entity mappings for CRM properties
     */
    async saveAvaEntityMappings() {
      const mappingsToSave = this.getAvaEntityContactMappings
      const errorNotifDuration = 5 * 1000
      if (!this.hasCompleteMappings(mappingsToSave)) {
        this.$notify({
          duration: errorNotifDuration,
          offset: 175,
          title: 'AVA Entity Mapping',
          message: 'One or more property mappings have no selected value',
          type: 'error',
          showClose: true
        })

        return false
      }

      const payload = {
        mappings: mappingsToSave,
        integration: this.integrationName
      }

      const res = await handleVuexApiCall(this.storeAvaEntityContactMappings, payload)

      if (!res.success) {
        let errors = res.response?.data?.errors ?? {}

        // Format error messages for display
        let errorMessages = []
        if (typeof errors === 'object') {
          Object.entries(errors).forEach(([key, messages]) => {
            if (Array.isArray(messages)) {
              errorMessages.push(...messages)
            } else if (typeof messages === 'string') {
              errorMessages.push(messages)
            }
          })
        }

        this.$notify({
          duration: errorNotifDuration,
          offset: 175,
          title: 'AVA Entity Mapping Errors',
          message:
            errorMessages.length > 0
              ? `<ul style="margin: 0; padding-left: 20px;">${errorMessages
                .map((msg) => `<li>${msg}</li>`)
                .join('')}</ul>`
              : 'Unable to save AVA entity mappings. Please contact customer support',
          type: 'error',
          showClose: true,
          dangerouslyUseHTMLString: true
        })

        return false
      }

      return true
    },
    hasCompleteMappings(mergedMappings) {
      // check if mappings all have values
      return mergedMappings.every(
        (mapping) => mapping['aloware_key'] && mapping['external_key']
      )
    },

    /**
     * Check if the Contact property mappings (CRM keys) have conflict with AVA Entity mappings (CRM keys) and vice versa
     */
    hasMappingsConflictWithCrmKeys() {
      if (this.integrationName !== HUBSPOT_INTEGRATION) return false

      // Get all contact property mappings (default + custom)
      const contactMappings = this.defaultPropertyMappings.concat(
        this.customPropertyMappings
      )

      // Get all AVA entity mappings
      const avaMappings = this.getAvaEntityContactMappings

      // Extract external_keys from both sets of mappings (filter out empty values)
      const contactExternalKeys = contactMappings
        .map(m => m.external_key)
        .filter(key => key)

      const avaExternalKeys = avaMappings
        .map(m => m.external_key)
        .filter(key => key)

      // Check for duplicates between the two sets
      const conflictingKeys = contactExternalKeys.filter(key =>
        avaExternalKeys.includes(key)
      )

      return conflictingKeys.length > 0
    },
    handleCallbackErrors() {
      // if authentication failed/blocked due to one CRM integration is already enabled.
      if (this.$route.query.conflict) {
        this.$notify({
          offset: 95,
          title: 'Integration Error',
          dangerouslyUseHTMLString: true,
          message: 'Only one CRM integration can be enabled.',
          type: 'error',
          showClose: true
        })

        // clean-up query so that when this page is reloaded same error will not show
        this.removePropertyFromQuery('conflict')
      }

      // if authentication failed/blocked due to a hubspot account is already integrated to another Aloware account
      if (this.$route.query.is_integration_error) {
        this.$notify({
          offset: 95,
          title: 'Integration Error',
          dangerouslyUseHTMLString: true,
          message: this.$route.query.message,
          type: 'error',
          showClose: true,
          duration: 10000
        })

        // clean-up query so that when this page is reloaded same error will not show
        this.removePropertyFromQuery('is_integration_error')
        this.removePropertyFromQuery('message')
      }

      // Handle error from query params (E.g. OAuth redirect returns with an error)
      if (this.$route.query.error) {
        this.model.last_connection_error = this.$route.query.error

        const isCallingAppError = this.$route.query.error_source === HUBSPOT_CALLING_INTEGRATION
        const errors = isCallingAppError
          ? [
              'Unable to connect to Aloware Calling for HubSpot.',
              this.$route.query.error,
            ]
          : [
              `Unable to connect to ${_.upperFirst(this.integrationName)}.`,
              `Integration connection error: Please re-authenticate by <a href="/integrations/${this.integrationName}/oauth"><b>clicking here</b></a>`,
            ]

        this.$root.handleErrors({ status: 422, data: { errors } })

        // clean-up query so that when this page is reloaded same error will not show
        this.removePropertyFromQuery('error')
        this.removePropertyFromQuery('error_source')
      }
    },
    handleResetTab() {
      if (this.activeTab === 'filters') {
        // No need to reset if the filter status is disabled
        if (!this.model.settings.filters.status) return

        // Call setToRecommendedValues from the filters pane component
        this.$refs.filtersPane.setToRecommendedValues()

        // Force the filters pane to re-render
        this.showFiltersPane = false
        this.$nextTick(() => {
          this.showFiltersPane = true
        })

        this.$notify({
          title: `${this.integrationNameWithCasing} Integration`,
          message: 'The Filters tab has been reset to default values',
          type: 'success'
        })
      } else {
        // Emit event for synchronization reset
        this.$root.$emit('integration-settings-reset-tab', {
          integrationName: this.integrationName,
          tabName: 'synchronization'
        })
      }
    },
    updateQueryParam(tab) {
      const query = Object.assign({}, this.$route.query)
      query['int-settings-tab'] = tab
      this.$router.replace({ query }).catch(() => {})
    },
    handleTabClick() {
      this.updateQueryParam(this.activeTab)
    },
    handleNextTab() {
      const currentIndex = this.availableTabs.indexOf(this.activeTab)
      // If at the last tab, go to the first tab, otherwise go to the next tab
      const nextIndex =
        currentIndex === this.availableTabs.length - 1 ? 0 : currentIndex + 1
      this.activeTab = this.availableTabs[nextIndex]
      this.updateQueryParam(this.activeTab)
    },
    handleBackTab() {
      const currentIndex = this.availableTabs.indexOf(this.activeTab)
      // If at the first tab, go to the last tab, otherwise go to the previous tab
      const prevIndex =
        currentIndex === 0 ? this.availableTabs.length - 1 : currentIndex - 1
      this.activeTab = this.availableTabs[prevIndex]
      this.updateQueryParam(this.activeTab)
    },
    async initializeCustomFields() {
      // fetch mappings for other integrations
      try {
        await Promise.all([
          this.getIntegrationPropertyMappings(this.integrationName),
          this.getIntegrationItemProperties(this.integrationName),
          this.getAlowareDefaultProperties(this.integrationName)
        ])
      } catch (e) {
        console.error(e)
        this.$notify({
          title: 'Contact Property Mapping',
          message:
            'Unable to fetch property mappings. Please contact customer support',
          type: 'error',
          showClose: true
        })
      }

      const mappings =
        this.$store.getters['hubspotPropertyMapping/getPropertyMappings']

      // Order the mappings so that the un-mappables are on top followed by contact owner
      this.defaultPropertyMappings = mappings
        .filter((mapping) => mapping.is_default)
        .sort((mapping) => (mapping.aloware_key === 'hubspot_owner' ? -1 : 1)) // push hubspot_owner to the top of the list
        .sort((mapping) => (!mapping.is_mappable ? -1 : 1)) // push un-mappable properties on top of the list

      this.customPropertyMappings = mappings.filter(
        (mapping) => !mapping.is_default
      )
    }
  },
  async created() {
    this.isInitializing = true
    await Promise.all([this.getStatics(), this.initializeSettings()])

    if (!this.model.status) {
      this.isInitializing = false
      return
    }

    // Only initialize custom fields if the integration is enabled and has required scopes
    if (
      this.integrationName !== GOHIGHLEVEL_INTEGRATION ||
      this.hasGoHighLevelRequiredScope
    ) {
      await this.initializeCustomFields()
    }

    // Handle save parameter
    if (this.$route.query.save) {
      // Set the model status to true to indicate that the integration is enabled
      // This condition is met when the user has successfully authenticated the integration
      this.model.status = true
      this.removePropertyFromQuery('save')
    }

    this.isInitializing = false
    // Wait for next tick to ensure tabs are rendered
    this.$nextTick(() => {
      // Set the active tab based on query parameter or default to 'connection'
      if (
        this.$route.query['int-settings-tab'] &&
        this.availableTabs.includes(this.$route.query['int-settings-tab'])
      ) {
        this.activeTab = this.$route.query['int-settings-tab']
      } else {
        this.activeTab = 'connection'
        this.updateQueryParam('connection')
      }
    })
  },
  mounted() {
    this.handleCallbackErrors()
  },
  beforeDestroy() {
    this.removePropertyFromQuery('int-settings-tab')
  }
}
</script>
<style scoped>
/* Style the tab content area */
.tab-head {
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* Keep headers fixed */
::v-deep .el-tabs__header {
  margin: 0;
  position: sticky;
  top: 0;
  background: white; /* This will prevent a see-through effect on the content area when the header is sticky */
  z-index: 2;
}

/* Make content area scrollable */
::v-deep .el-tabs__content {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

/* Ensure full height for right/left positioned tab content */
.el-tabs--right .el-tabs__content,
.el-tabs--left .el-tabs__content {
  height: 100%;
}

/* Set default tab text style - darker color and bold */
::v-deep .el-tabs__item {
  font-weight: bold !important;
  font-size: 15px !important;
  color: #333333 !important;
  padding: 0 11px !important;
}

/* Keep text color consistent on hover (prevent blue hover state) */
::v-deep .el-tabs__item:hover {
  color: #000000 !important;
}

/* Keep text color consistent when active (prevent green active state) */
::v-deep .el-tabs__item.is-active {
  color: #000000 !important;
}

/* Style the active tab indicator - green and thicker */
::v-deep .el-tabs__active-bar {
  background-color: #00bf50 !important;
  height: 3px !important;
}

/* Make the bottom border (grey line) thicker to match active bar */
::v-deep .el-tabs__nav-wrap::after {
  height: 3px !important;
}

/* Button hover and focus states for type="default" buttons */
::v-deep .el-button--default:hover,
::v-deep .el-button--default:focus,
::v-deep .el-button--default:active {
  background-color: #e6f7ed !important; /* Light green background */
  border-color: #00bf50 !important;
  color: #00854a !important; /* Darker green text */
}

.main-content {
  overflow-y: auto;
  max-height: calc(100% - 70px); /* Increased space for buttons */
  height: calc(100% - 70px); /* Force the height to be consistent */
  padding-bottom: 20px;
}

.action-buttons {
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  background-color: white;
  padding: 10px 3%;
}

.left-buttons {
  display: flex;
  align-items: center;
}

.right-buttons {
  display: flex;
  align-items: center;
}

.mr-2 {
  margin-right: 8px;
}

/* Style scrollbar for Webkit browsers */
.main-content::-webkit-scrollbar {
  width: 8px;
}

.main-content::-webkit-scrollbar-track {
  background: transparent;
}

.main-content::-webkit-scrollbar-thumb {
  background-color: #dcdfe6;
  border-radius: 4px;
}

.main-content::-webkit-scrollbar-thumb:hover {
  background-color: #c0c4cc;
}

/* Style scrollbar for Firefox */
.main-content {
  scrollbar-width: thin;
  scrollbar-color: #dcdfe6 transparent;
}

/* Disabled tab content styles */
.disabled-tab-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  text-align: center;
  padding: 40px 20px;
}

.disabled-tab-message {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  margin-bottom: 20px;
}

.disabled-tab-message i {
  font-size: 20px;
  color: #e6a23c;
}
</style>
